// utils/fulfillment.js

// --- Internal cache of store locations (set once from AppConfig) ---
let STORE_LOCATIONS = []; // e.g., ["1483 Topsail Rd.", "16 Stavanger Dr."]

// Call this once when AppConfig loads (see usage below)
export const setStoreLocations = (locs = []) => {
  STORE_LOCATIONS = Array.from(
    new Set((locs || []).map((s) => String(s || "").trim()).filter(Boolean))
  );
};

export const getStoreLocations = () => STORE_LOCATIONS.slice();

// Convenience if you get the whole config object
export const initStoreLocationsFromConfig = (config) => {
  const locs = config?.delivery?.pickupLocations || [];
  setStoreLocations(locs);
};

// --- Helpers to detect delivery and normalize addresses ---
export const isDelivery = (fulfillment = {}) => {
  const method = (fulfillment.method || "").toLowerCase();
  return (
    method.includes("delivery") ||
    !!fulfillment.delivery ||
    !!fulfillment.deliveryAddress ||
    !!fulfillment.address
  );
};

// pick a default store for deliveries (first from config)
export const getDefaultDeliveryStore = () =>
  STORE_LOCATIONS[0] || "1483 Topsail Rd.";

// Best-effort map of a raw location string to one of STORE_LOCATIONS
const clean = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/[.,']/g, "")
    .replace(/\s+/g, " ")
    .trim();

const canonicalFromStoreList = (raw = "") => {
  const rawTrim = String(raw || "").trim();
  if (!rawTrim) return "";

  // Exact
  if (STORE_LOCATIONS.includes(rawTrim)) return rawTrim;

  // Fuzzy
  const key = clean(rawTrim);
  const hit =
    STORE_LOCATIONS.find((l) => clean(l) === key) ||
    STORE_LOCATIONS.find((l) => clean(l).startsWith(key)) ||
    STORE_LOCATIONS.find((l) => key.startsWith(clean(l)));

  return hit || rawTrim; // fallback to raw if we truly don't recognize it
};

// Normalize the location we use for filtering/sorting
export const normalizeLocation = (fulfillment = {}) => {
  if (isDelivery(fulfillment)) return getDefaultDeliveryStore();
  return canonicalFromStoreList(fulfillment.location || "");
};

// Legacy collections sometimes come as strings; normalize those too
export const normalizeCollectionLocation = (loc = "") =>
  canonicalFromStoreList(loc);

// Human-readable delivery address
export const deliveryAddressString = (fulfillment = {}) => {
  const addr =
    fulfillment.deliveryAddress ||
    fulfillment.address ||
    fulfillment.delivery?.address;

  if (typeof addr === "string") return addr;

  if (addr && typeof addr === "object") {
    const {
      line1,
      line2,
      address1,
      address2,
      street,
      city,
      province,
      state,
      postalCode,
      zip,
      country,
    } = addr;
    const parts = [
      line1 || address1 || street,
      line2 || address2,
      city,
      province || state,
      postalCode || zip,
      country,
    ].filter(Boolean);
    if (parts.length) return parts.join(", ");
  }

  // Fallback: legacy orders might have put the full address in `location`
  if (isDelivery(fulfillment) && typeof fulfillment.location === "string") {
    return fulfillment.location;
  }
  return "";
};
